const SHARED_COOKIE_ROOTS = [
  'thekairos.app',
  'klarora.app',
  'klarora.ai',
  'klarora.nl',
  'klarora.eu',
] as const;

function normalizeHostname(hostname: string | null | undefined): string {
  return (hostname ?? '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')
    .replace(/^www\./, '');
}

export function rootDomainForHost(hostname: string | null | undefined): string | null {
  const normalized = normalizeHostname(hostname);
  if (!normalized) return null;

  return SHARED_COOKIE_ROOTS.find(
    (root) => normalized === root || normalized.endsWith(`.${root}`),
  ) ?? null;
}

export function sharedCookieDomainForHost(hostname: string | null | undefined): string | undefined {
  const root = rootDomainForHost(hostname);
  return root ? `.${root}` : undefined;
}

export function dashboardOriginForHost(
  hostname: string | null | undefined,
  fallback = 'https://dashboard.thekairos.app',
): string {
  const root = rootDomainForHost(hostname);
  return root ? `https://dashboard.${root}` : fallback;
}

export function landingOriginForHost(
  hostname: string | null | undefined,
  fallback = 'https://thekairos.app',
): string {
  const root = rootDomainForHost(hostname);
  return root ? `https://${root}` : fallback;
}

/** Browser origins owned by the first-party app estate. No suffix wildcards. */
const APP_ROOTS = ['thekairos.app', 'klarora.app', 'klarora.ai', 'klarora.nl', 'klarora.eu'] as const;
const APP_PREFIXES = ['', 'www', 'dashboard', 'admin', 'admin-new', 'finance', 'cms', 'auth'] as const;
export const FIRST_PARTY_APP_ORIGINS = APP_ROOTS.flatMap((root) =>
  APP_PREFIXES.map((prefix) => `https://${prefix ? `${prefix}.` : ''}${root}`),
);
export const FIRST_PARTY_ADMIN_ORIGINS = APP_ROOTS.flatMap((root) =>
  ['admin', 'admin-new'].map((prefix) => `https://${prefix}.${root}`),
);
const trustedApps = new Set(FIRST_PARTY_APP_ORIGINS);

export function isTrustedAppOrigin(origin: string | null | undefined): boolean {
  if (!origin) return false;
  if (trustedApps.has(origin)) return true;
  // Desktop's local server uses an ephemeral port. Accept only serialized
  // loopback HTTP origins, never credentials, paths, arbitrary subdomains or null.
  if (!/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin)) return false;
  try { return new URL(origin).origin === origin; } catch { return false; }
}
